import HeaderPortal from "components/portal/header-portal";
import ReactList, {
  ReactListEmpty,
  ReactListInitialLoader,
  ReactListItems,
  ReactListPagination,
  ReactListProvider,
  ReactListSearch,
} from "@7span/react-list";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "ui/components/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/components/select";
import { Button } from "ui/components/button";
import CommonPagination from "./pagination";
import { EmptySection1 } from "../empty";
import { EvtHeader } from "components/header";
import Icon from "../icon";
import { LIST_FILTER } from "icons";
import ListLoader from "./loader";
import React from "react";
import Search from "./search";
import Table from "./table";
import classNames from "classnames";
import { getListConfig } from "components/list/react-list";
import { fetchListData } from "components/list/useListQuery";
import { eventBus } from "utils/eventBus";
import { useEffect } from "react";
import { useState } from "react";
import useUrlListState from "./use-url-list-state";
import { LIST_VERSION } from "./constant";

const ReactListWrapper = ({
  endPoint,
  columns = [],
  title,
  className,
  showHeader = true,
  hidePagination = false,
  showSearch = true,
  page = 1,
  rowClassName = "h-10",
  getRowClassName, // Optional fn: (rowOriginal) => string
  perPage = 10,
  search = "",
  sortBy = "date_created",
  sortOrder = "desc",
  href = () => { },
  onClick,
  renderFilters,
  searchPlaceholder = "Search",
  meta = {},
  teleportFilterButton = false,
  filters = {},
  renderItems,
  renderItemsLoader,
  onDataChange = () => { },
  onResponseChange = () => { },
  storageKey,
  teleportFilters,
  hideFilterButton = false,
  emptySectionTitle,
  emptySectionDescription,
  emptySectionDescriptionVariant,
  showListHeader = true,
  onFiltersChange,
  renderContent = () => { },
  inlineFilters = false,
  enableAccordion = false, // Enable accordion functionality
  renderExpandedRow, // Function to render content when row is expanded
  expandedRowClassName = "", // Additional className for expanded row content
  showIndex = true,
  hideIndex = false,
  selection = false,
  value = [],
  onChange = () => { },
  persistFilters = true, // When false, filters/search are not saved to localStorage
  custom = false,
}) => {
  const config = React.useMemo(
    () => ({
      ...getListConfig({ persistFilters }),
      requestHandler: (params) => fetchListData({ ...params, custom }),
    }),
    [persistFilters, custom],
  );
  // Apply ?filters=&search= from URL before ReactList mounts.
  useUrlListState({
    endpoint: endPoint,
    version: storageKey + LIST_VERSION,
    defaultFilters: filters,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [selectedItemsMap, setSelectedItemsMap] = useState({});

  useEffect(() => {
    const selection = {};
    const newItems = {};
    if (value) {
      const arr = Array.isArray(value) ? value : [value];
      arr.forEach((item) => {
        const id = typeof item === "object" ? item.id : item;
        if (id) {
          selection[id] = true;
          if (typeof item === "object") {
            newItems[id] = item;
          }
        }
      });
    }

    setRowSelection((prev) => {
      const prevKeys = Object.keys(prev);
      const selKeys = Object.keys(selection);
      if (prevKeys.length !== selKeys.length) return selection;
      for (let key of prevKeys) {
        if (prev[key] !== selection[key]) return selection;
      }
      return prev;
    });

    if (Object.keys(newItems).length > 0) {
      setSelectedItemsMap((prev) => {
        let changed = false;
        const newMap = { ...prev };
        for (const [id, item] of Object.entries(newItems)) {
          if (newMap[id] !== item) {
            newMap[id] = item;
            changed = true;
          }
        }
        return changed ? newMap : prev;
      });
    }
  }, [value]);

  const handleRowSelectionChange = (updater) => {
    let newSelection = {};
    if (typeof updater === 'function') {
      newSelection = updater(rowSelection);
    } else {
      newSelection = updater;
    }
    setRowSelection(newSelection);

    const selectedObjects = Object.keys(newSelection).filter(k => newSelection[k]).map(id => selectedItemsMap[id] || { id });
    onChange(selectedObjects);
  };
  return (
    <ReactListProvider config={config}>
      <ReactList
        title={title}
        endpoint={endPoint}
        custom={custom}
        search={search}
        page={page}
        filters={filters}
        perPage={perPage}
        sortBy={sortBy}
        sortOrder={sortOrder}
        showHeader={showHeader}
        paginationMode="pagination"
        version={storageKey + LIST_VERSION}
        meta={meta}
      >
        {({
          filters: currentFilters,
          setSort,
          setFilters,
          count,
          loader,
          sort: { sortBy, sortOrder },
          data,
          hasActiveFilters,
          clearFilters,
          response,
          refresh,
          setPerPage,
          pagination,
        }) => {
          const onDataChangeRef = React.useRef(onDataChange);
          React.useEffect(() => {
            onDataChangeRef.current = onDataChange;
          }, [onDataChange]);

          useEffect(() => {
            if (data && data.length > 0) {
              onDataChangeRef.current(data);

              if (selection) {
                setSelectedItemsMap((prev) => {
                  let changed = false;
                  const newMap = { ...prev };
                  data.forEach((item) => {
                    if (newMap[item.id] !== item) {
                      newMap[item.id] = item;
                      changed = true;
                    }
                  });
                  return changed ? newMap : prev;
                });
              }
            }
          }, [data, selection]);

          const onResponseChangeRef = React.useRef(onResponseChange);
          React.useEffect(() => {
            onResponseChangeRef.current = onResponseChange;
          }, [onResponseChange]);

          useEffect(() => {
            if (response) {
              onResponseChangeRef.current(response);
            }
          }, [response]);

          useEffect(() => {
            if (onFiltersChange) {
              onFiltersChange(currentFilters);
            }
          }, [currentFilters, onFiltersChange]);

          const refreshKey = `${endPoint}${storageKey}`;

          useEffect(() => {
            const handleRefresh = (id) => {
              if (id === refreshKey) {
                refresh();
              }
            };

            eventBus.on("refresh:list", handleRefresh);

            return () => {
              eventBus.off("refresh:list", handleRefresh);
            };
          }, [refresh, refreshKey]);

          return (
            <div className="flex-1 overflow-auto">
              {!teleportFilterButton && showListHeader && title ? (
                <EvtHeader title={title}>
                  <div className="flex gap-4 items-stretch">
                    {showSearch ? (
                      <ReactListSearch>
                        {(props) => (
                          <Search
                            {...props}
                            searchPlaceholder={searchPlaceholder}
                          />
                        )}
                      </ReactListSearch>
                    ) : null}
                    {renderFilters && !teleportFilters ? (
                      <>
                        {inlineFilters ? (
                          <div>
                            {renderFilters({
                              setIsOpen,
                              filters: currentFilters,
                              setFilters,
                              isOpen: true,
                              defaultFilters: filters,
                              clearFilters,
                            })}
                          </div>
                        ) : (
                          <Button
                            variant={"outline"}
                            onClick={() => setIsOpen(!isOpen)}
                            className="text-sm font-medium h-full"
                          >
                            <div className="relative">
                              <Icon
                                icon={LIST_FILTER}
                                className="size-5 text-primary-500"
                              />
                              {hasActiveFilters && (
                                <span className="absolute bg-red-500 size-2 top-0 right-0 rounded-full" />
                              )}
                            </div>
                            Filters
                          </Button>
                        )}
                      </>
                    ) : null}
                  </div>
                </EvtHeader>
              ) : null}
              {renderContent()}
              {teleportFilterButton && !hideFilterButton ? (
                <HeaderPortal>
                  <Button
                    variant={"outline"}
                    onClick={() => setIsOpen(!isOpen)}
                  >
                    <div className="relative">
                      <Icon icon={LIST_FILTER} className="size-4" />
                      {hasActiveFilters && (
                        <span className="absolute bg-red-500 size-2 top-0 right-0 rounded-full" />
                      )}
                    </div>
                    Filters
                  </Button>{" "}
                </HeaderPortal>
              ) : null}
              <div
                className={classNames(
                  {
                    "p-6": !teleportFilterButton && !showListHeader,
                    " !p-0": renderItems,
                  },
                  "px-4.5",
                  className,
                )}
              >
                <ReactListInitialLoader>
                  {renderItems ? (
                    renderItemsLoader?.()
                  ) : (
                    <ListLoader
                      columns={columns}
                      rows={perPage}
                      showHeader={showHeader}
                    />
                  )}
                </ReactListInitialLoader>

                <ReactListEmpty>
                  <EmptySection1
                    title={emptySectionTitle}
                    description={emptySectionDescription}
                    variant={emptySectionDescriptionVariant}
                  />
                </ReactListEmpty>

                <ReactListItems>
                  {({ items }) => {
                    if (renderItems) {
                      return renderItems({ items });
                    }
                    const shouldShowIndex = showIndex && !hideIndex;
                    const updatedColumns = shouldShowIndex
                      ? [
                          {
                            id: "#",
                            header: "#",
                            size: 50,
                            cell: ({ row }) => {
                              const page = pagination?.page || 1;
                              const perPage = pagination?.perPage || 10;
                              return (
                                <span>
                                  {(page - 1) * perPage + row.index + 1}
                                </span>
                              );
                            },
                          },
                          ...columns,
                        ]
                      : columns;
                    return (
                      <Table
                        data={items}
                        columns={updatedColumns}
                        onSort={setSort}
                        href={href}
                        onClick={onClick}
                        rows={perPage}
                        loader={loader}
                        showHeader={showHeader}
                        rowClassName={rowClassName} // Add this line to set row height
                        getRowClassName={getRowClassName}
                        endPoint={endPoint}
                        refresh={refresh}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        enableAccordion={enableAccordion}
                        renderExpandedRow={renderExpandedRow}
                        expandedRowClassName={expandedRowClassName}
                        selection={selection}
                        rowSelection={rowSelection}
                        setRowSelection={handleRowSelectionChange}
                      />
                    );
                  }}
                </ReactListItems>
                {!hidePagination && count > 0 && (
                  <div className="flex justify-between items-center my-6">
                    <div className="flex items-center gap-4">
                      <Select
                        value={String(pagination.perPage || 10)}
                        onValueChange={(val) => setPerPage(Number(val))}
                      >
                        <SelectTrigger className="w-fit gap-1 h-8 text-sm text-muted-foreground border-none shadow-none bg-transparent hover:bg-muted py-0 px-2 cursor-pointer focus:ring-0 px-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="text-sm text-muted-foreground">
                        Showing {((pagination?.page || 1) - 1) * (pagination?.perPage || 10) + 1} to {Math.min((pagination?.page || 1) * (pagination?.perPage || 10), count)} of {count} entries
                      </div>
                    </div>
                    <ReactListPagination>
                      {(props) => (
                        <CommonPagination
                          {...props}
                          className="justify-end w-max ml-auto mr-0"
                          disable={loader.isLoading}
                        />
                      )}
                    </ReactListPagination>
                  </div>
                )}
              </div>
              {renderFilters ? (
                teleportFilters ? (
                  <HeaderPortal>
                    {renderFilters({
                      setIsOpen,
                      filters: currentFilters,
                      setFilters,
                      isOpen,
                      defaultFilters: filters,
                      clearFilters,
                    })}
                  </HeaderPortal>
                ) : (
                  !inlineFilters && (
                    <Sheet open={isOpen} onOpenChange={setIsOpen}>
                      <SheetContent className="gap-2">
                        <SheetHeader className="pt-6 px-6 pb-4">
                          <SheetTitle className="text-2xl font-semibold">
                            Filters
                          </SheetTitle>
                        </SheetHeader>
                        <div className="w-full overflow-auto h-full">
                          {renderFilters({
                            setIsOpen,
                            filters: currentFilters,
                            setFilters,
                            isOpen,
                            defaultFilters: filters,
                            clearFilters,
                          })}
                        </div>
                      </SheetContent>
                    </Sheet>
                  )
                )
              ) : null}
            </div>
          );
        }}
      </ReactList>
    </ReactListProvider>
  );
};

export default React.memo(ReactListWrapper);
